/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import styles from './styles.module.scss';
import { useLabel } from '@/hooks/useLabels';
import cx from 'classnames';
import { ButtonBase } from '../ButtonBase/ButtonBase';

type Props = {
  numberOfDots: number;
  currentSlide: number;
  handleDotClick?: (index: number, setFocusOnAnchor: boolean) => void;
  slidesContainerId: string;
};

function CarouselDotsComponent({
  numberOfDots,
  currentSlide,
  handleDotClick,
  slidesContainerId,
}: Props) {
  const goToSlideLabel = useLabel('label_go_to_slide');

  if (numberOfDots <= 1) {
    return null;
  }

  const onClickHandler = (e: React.MouseEvent<HTMLElement>, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    handleDotClick?.(index, false);
  };

  const createDots = () => {
    return Array.from(
      {
        length: numberOfDots,
      },
      (_, index) => {
        const isActive = index === currentSlide;
        return (
          <li key={`carousel-button-${index + 1}`} className={styles.carouselDotListItem}>
            <ButtonBase
              className={cx(styles.carouselDot, { [styles.active]: isActive })}
              aria-label={`${goToSlideLabel} ${index + 1}`}
              aria-controls={slidesContainerId}
              aria-selected={isActive}
              id={`${slidesContainerId}-tab-${index + 1}`}
              onClick={(e: React.MouseEvent<HTMLElement>) => onClickHandler(e, index)}
              role="tab"
              tabIndex={isActive ? 0 : -1}
            />
          </li>
        );
      },
    );
  };

  return (
    <div
      data-testid="carousel-indicator-container"
      className={styles.carouselDotsCenter}
      role="tablist"
      aria-label="Carousel navigation dots"
      aria-controls={slidesContainerId}
    >
      <ul className={styles.carouselDotsList}>{createDots()}</ul>
    </div>
  );
}

export const CarouselDots = React.memo(CarouselDotsComponent);
